import { getFallbackPathForRole, hasPermission, isForecastViewerRole, isManufacturingBlockedRole, isPlanningVisibleManufacturingPath, isSuperAdmin, roleMatches } from '@/permissions';
import { ErrorBoundary } from '@components/common/ErrorBoundary';
import { BILLING_ENABLED, DATA_SOURCES_ENABLED, GETTING_STARTED_ENABLED, SECURITY_ENABLED, SELF_SIGNUP_ENABLED } from '@/lib/deployment-features';
import { SessionRenewalPrompt } from '@components/common/SessionRenewalPrompt';
import { PageSkeleton } from '@components/ui/PageSkeleton';
import { useSettings } from '@hooks/useSettings';
import { useAuthStore } from '@stores/auth.store';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';

// Layout
const AuthLayout = lazy(() => import('@components/layout/AuthLayout'));
const MainLayout = lazy(() => import('@components/layout/MainLayout'));
const LicenseGate = lazy(() => import('@components/layout/LicenseGate'));

// Pages
const Dashboard = lazy(() => import('@pages/Dashboard'));
const NotFound = lazy(() => import('@pages/NotFound'));
const ForgotPassword = lazy(() => import('@pages/auth/ForgotPassword'));
const Login = lazy(() => import('@pages/auth/Login'));
// Inline env checks let Vite prune disabled imports before discovering their page chunks.
const Signup = import.meta.env.VITE_SELF_SIGNUP_ENABLED === 'true' ? lazy(() => import('@pages/auth/Signup')) : NotFound;
const ForceResetPassword = lazy(() => import('@pages/auth/ForceResetPassword'));
const SsoReturn = lazy(() => import('@pages/auth/SsoReturn'));
const Actuals = lazy(() => import('@pages/data/Actuals'));
const DataImport = lazy(() => import('@pages/data/DataImport'));
const Dimensions = lazy(() => import('@pages/data/Dimensions'));
const ProductMaster = lazy(() => import('@pages/data/ProductMaster'));
const Locations = lazy(() => import('@pages/data/Locations'));
const ForecastDashboard = lazy(() => import('@pages/forecasts/ForecastDashboard'));
const ForecastDetail = lazy(() => import('@pages/forecasts/ForecastDetail'));
const Forecasts = lazy(() => import('@pages/forecasts/Forecasts'));
const ManufacturingRoutes = lazy(() => import('@pages/manufacturing/ManufacturingRoutes'));
const CreatePlan = lazy(() => import('@pages/plans/CreatePlan'));
const PlanDetail = lazy(() => import('@pages/plans/PlanDetail'));
const Plans = lazy(() => import('@pages/plans/Plans'));
const Reports = lazy(() => import('@pages/reports/Reports'));
const AiReporting = lazy(() => import('@pages/reports/AiReporting'));
const InsightsDashboard = lazy(() => import('@pages/insights/InsightsDashboard'));
const PharmaReportsRoutes = lazy(() => import('@pages/pharma-reports/PharmaReportsRoutes'));
const Scenarios = lazy(() => import('@pages/scenarios/Scenarios'));
const AuditLog = lazy(() => import('@pages/settings/AuditLog'));
const Notifications = lazy(() => import('@pages/settings/Notifications'));
const Profile = lazy(() => import('@pages/settings/Profile'));
const MargEde = lazy(() => import('@pages/settings/MargEde'));
const DataSources = import.meta.env.VITE_DATA_SOURCES_ENABLED === 'true' ? lazy(() => import('@pages/settings/data-sources/DataSources')) : NotFound;
const GettingStarted = import.meta.env.VITE_GETTING_STARTED_ENABLED === 'true' ? lazy(() => import('@pages/settings/GettingStarted')) : NotFound;
const Billing = import.meta.env.VITE_BILLING_ENABLED === 'true' ? lazy(() => import('@pages/settings/billing/Billing')) : NotFound;
const Security = import.meta.env.VITE_SECURITY_ENABLED === 'true' ? lazy(() => import('@pages/settings/security/Security')) : NotFound;
const BillingReturn = import.meta.env.VITE_BILLING_ENABLED === 'true' ? lazy(() => import('@pages/settings/billing/BillingReturn')) : NotFound;
const BillingReceipt = import.meta.env.VITE_BILLING_ENABLED === 'true' ? lazy(() => import('@pages/settings/billing/BillingReceipt')) : NotFound;
const SubscriptionsAdmin = import.meta.env.VITE_BILLING_ENABLED === 'true' ? lazy(() => import('@pages/platform/SubscriptionsAdmin')) : NotFound;
const Settings = lazy(() => import('@pages/settings/Settings'));
const Users = lazy(() => import('@pages/settings/Users'));
const Roles = lazy(() => import('@pages/settings/Roles'));
const PlatformDashboard = lazy(() => import('@pages/platform/PlatformDashboard'));
const TenantManage = lazy(() => import('@pages/platform/TenantManage'));
const AiBilling = lazy(() => import('@pages/billing/AiBilling'));
const AiBillingAdmin = lazy(() => import('@pages/platform/AiBillingAdmin'));

function RouteFallback() {
  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 p-4">
      <PageSkeleton />
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 p-4">
        <PageSkeleton />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Force password reset before accessing any protected route
  if (user?.mustResetPassword) {
    return <Navigate to="/force-reset-password" replace />;
  }

  return <>{children}</>;
}

// Public Route Component (redirect to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to={getFallbackPathForRole(user?.role)} replace />;
  }

  return <>{children}</>;
}

function HomeRoute() {
  const role = useAuthStore((s) => s.user?.role);
  return <Navigate to={getFallbackPathForRole(role)} replace />;
}

function RoleAwareRoute({
  restrictForecastViewer = false,
  restrictManufacturing = false,
  requiredModule,
  children,
}: {
  restrictForecastViewer?: boolean;
  restrictManufacturing?: boolean;
  requiredModule?: string;
  children: React.ReactNode;
}) {
  const role = useAuthStore((s) => s.user?.role);
  const { data: settings, isLoading: isSettingsLoading } = useSettings();
  const location = useLocation();

  // SUPER_ADMIN is never blocked
  if (isSuperAdmin(role)) return <>{children}</>;

  // Forecast planner/viewer roles get read-only access to specific planning-relevant
  // manufacturing surfaces (PO, PI, Inventory, Suppliers, Batches) — bypass the
  // restrictManufacturing guard for those paths.
  const planningPathException =
    restrictManufacturing &&
    isManufacturingBlockedRole(role) &&
    isPlanningVisibleManufacturingPath(location.pathname);

  const blocked =
    (restrictForecastViewer && isForecastViewerRole(role) && !planningPathException) ||
    (restrictManufacturing && isManufacturingBlockedRole(role) && !planningPathException);

  if (!blocked && requiredModule && isSettingsLoading) {
    return <RouteFallback />;
  }

  const moduleDisabled = Boolean(
    requiredModule &&
    settings?.enabledModules &&
    settings.enabledModules[requiredModule as keyof NonNullable<typeof settings.enabledModules>] === false,
  );

  if (blocked || moduleDisabled) {
    return <Navigate to={getFallbackPathForRole(role)} replace />;
  }

  return <>{children}</>;
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const role = useAuthStore((s) => s.user?.role);
  if (!isSuperAdmin(role)) {
    return <Navigate to={getFallbackPathForRole(role)} replace />;
  }
  return <>{children}</>;
}

function TenantOnlyRoute({ children }: { children: React.ReactNode }) {
  const role = useAuthStore((s) => s.user?.role);
  if (isSuperAdmin(role)) {
    return <Navigate to="/platform" replace />;
  }
  return <>{children}</>;
}

function AdminOnlyRoute({ children }: { children: React.ReactNode }) {
  const role = useAuthStore((s) => s.user?.role);
  return roleMatches(role, 'ADMIN') ? <>{children}</> : <Navigate to={getFallbackPathForRole(role)} replace />;
}

function UserManagementRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  return hasPermission(user, 'users:read') ? <>{children}</> : <Navigate to={getFallbackPathForRole(user?.role)} replace />;
}

export default function App() {
  return (
    <ErrorBoundary>
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public routes */}
      <Route element={<AuthLayout />}>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        {SELF_SIGNUP_ENABLED && <Route
          path="/signup"
          element={
            <PublicRoute>
              <Signup />
            </PublicRoute>
          }
        />}
        {/* Google or Microsoft hands back a one-time pass; it starts a new session even over an old one */}
        <Route path="/login/sso" element={<SsoReturn />} />
        {/* Force password reset — authenticated but must change password */}
        <Route path="/force-reset-password" element={<ForceResetPassword />} />
      </Route>

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <TenantOnlyRoute>
              <LicenseGate>
                <MainLayout />
              </LicenseGate>
            </TenantOnlyRoute>
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<HomeRoute />} />
        <Route path="/dashboard" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />

        {/* Plans */}
        <Route path="/plans" element={<Plans />} />
        <Route path="/plans/new" element={<CreatePlan />} />
        <Route path="/plans/:id" element={<PlanDetail />} />

        {/* Forecasts */}
        <Route path="/forecasts" element={<ErrorBoundary><Forecasts /></ErrorBoundary>} />
        <Route path="/forecasts/dashboard" element={<ErrorBoundary><ForecastDashboard /></ErrorBoundary>} />
        <Route path="/forecasts/:id" element={<ErrorBoundary><ForecastDetail /></ErrorBoundary>} />

        {/* Scenarios */}
        <Route path="/scenarios" element={<Scenarios />} />

        {/* Data Management */}
        <Route path="/data/import" element={<RoleAwareRoute restrictForecastViewer requiredModule="data"><DataImport /></RoleAwareRoute>} />
        <Route path="/data/actuals" element={<RoleAwareRoute restrictForecastViewer requiredModule="data"><Actuals /></RoleAwareRoute>} />
        <Route path="/data/dimensions" element={<RoleAwareRoute restrictForecastViewer requiredModule="data"><Dimensions /></RoleAwareRoute>} />
        <Route path="/data/products" element={<RoleAwareRoute restrictForecastViewer requiredModule="data"><ProductMaster /></RoleAwareRoute>} />
        <Route path="/data/locations" element={<RoleAwareRoute restrictForecastViewer requiredModule="data"><Locations /></RoleAwareRoute>} />

        {/* Reports */}
        <Route path="/reports" element={<Reports />} />
        <Route path="/reports/ai" element={<RoleAwareRoute requiredModule="ai-reporting"><AiReporting /></RoleAwareRoute>} />
        <Route path="/insights" element={<RoleAwareRoute requiredModule="ai-reporting"><InsightsDashboard /></RoleAwareRoute>} />
        <Route path="/billing/ai" element={<RoleAwareRoute requiredModule="ai-reporting"><AiBilling /></RoleAwareRoute>} />
        <Route path="/pharma-reports/*" element={<RoleAwareRoute requiredModule="reports"><PharmaReportsRoutes /></RoleAwareRoute>} />

        {/* Settings */}
        <Route path="/settings" element={<RoleAwareRoute restrictForecastViewer><Settings /></RoleAwareRoute>} />
        <Route path="/settings/users" element={<UserManagementRoute><Users /></UserManagementRoute>} />
        <Route path="/settings/roles" element={<SuperAdminRoute><Roles /></SuperAdminRoute>} />
        {GETTING_STARTED_ENABLED && <Route path="/getting-started" element={<AdminOnlyRoute><GettingStarted /></AdminOnlyRoute>} />}
        {BILLING_ENABLED && <Route path="/settings/billing" element={<AdminOnlyRoute><Billing /></AdminOnlyRoute>} />}
        {SECURITY_ENABLED && <Route path="/settings/security" element={<AdminOnlyRoute><Security /></AdminOnlyRoute>} />}
        {BILLING_ENABLED && <Route path="/billing/return/:gateway/:paymentId/*" element={<AdminOnlyRoute><BillingReturn /></AdminOnlyRoute>} />}
        <Route path="/settings/marg-ede" element={<RoleAwareRoute restrictForecastViewer requiredModule="marg-ede"><MargEde /></RoleAwareRoute>} />
        {DATA_SOURCES_ENABLED && <Route path="/settings/data-sources" element={<AdminOnlyRoute><RoleAwareRoute requiredModule="marg-ede"><DataSources /></RoleAwareRoute></AdminOnlyRoute>} />}
        <Route path="/settings/profile" element={<Profile />} />
        <Route path="/settings/audit-log" element={<RoleAwareRoute restrictForecastViewer><AuditLog /></RoleAwareRoute>} />
        <Route path="/notifications" element={<Notifications />} />

        {/* Manufacturing */}
        <Route path="/manufacturing/*" element={<RoleAwareRoute restrictManufacturing requiredModule="manufacturing"><ManufacturingRoutes /></RoleAwareRoute>} />
      </Route>

      <Route
        element={
          <ProtectedRoute>
            <SuperAdminRoute>
              <MainLayout />
            </SuperAdminRoute>
          </ProtectedRoute>
        }
      >
        <Route path="/platform" element={<PlatformDashboard />} />
        <Route path="/platform/tenants/:id" element={<TenantManage />} />
        <Route path="/platform/ai-billing" element={<AiBillingAdmin />} />
        {BILLING_ENABLED && <Route path="/platform/subscriptions" element={<SubscriptionsAdmin />} />}
      </Route>

      {/* Printable receipt, outside the app layout */}
      {BILLING_ENABLED && <Route
        path="/settings/billing/receipts/:paymentId"
        element={
          <ProtectedRoute>
            <TenantOnlyRoute>
              <AdminOnlyRoute>
                <BillingReceipt />
              </AdminOnlyRoute>
            </TenantOnlyRoute>
          </ProtectedRoute>
        }
      />}

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    <SessionRenewalPrompt />
    </Suspense>
    </ErrorBoundary>
  );
}
